import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Zap } from 'lucide-react';

const CelebrationAnimation = ({ show }) => {
  const confettiPieces = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 3 + Math.random() * 2,
    color: ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'][Math.floor(Math.random() * 5)]
  }));

  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-50 pointer-events-none overflow-hidden">
          {confettiPieces.map((piece) => (
            <motion.div
              key={piece.id}
              initial={{
                x: `${piece.x}vw`,
                y: '-10vh',
                rotate: 0,
                opacity: 1
              }}
              animate={{
                y: '110vh',
                rotate: 360 * 3,
                opacity: 0
              }}
              transition={{
                duration: piece.duration,
                delay: piece.delay,
                ease: 'easeOut'
              }}
              className="absolute w-3 h-3 rounded-sm"
              style={{ backgroundColor: piece.color }}
            />
          ))}

          <div className="flex items-center justify-center h-full">
            <motion.div
              initial={{ scale: 0, rotate: 0 }}
              animate={{ scale: 1, rotate: [0, 5, -5, 0] }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{
                type: 'spring',
                stiffness: 200,
                damping: 15,
                rotate: {
                  duration: 0.6,
                  repeat: 3
                }
              }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-full blur-xl opacity-60 animate-pulse" />

              <div className="relative bg-white dark:bg-dark-800 rounded-3xl shadow-2xl p-8 text-center border-4 border-yellow-400">
                <motion.div
                  animate={{
                    y: [-10, 10, -10],
                    rotate: [0, 360],
                  }}
                  transition={{
                    y: {
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    },
                    rotate: {
                      duration: 3,
                      repeat: Infinity,
                      ease: 'linear'
                    }
                  }}
                  className="absolute -top-6 -left-6 w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg"
                >
                  <Trophy className="w-6 h-6 text-white" />
                </motion.div>

                <motion.div
                  animate={{
                    y: [10, -10, 10],
                    rotate: [360, 0],
                  }}
                  transition={{
                    y: {
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: 0.5
                    },
                    rotate: {
                      duration: 3,
                      repeat: Infinity,
                      ease: 'linear',
                      delay: 0.5
                    }
                  }}
                  className="absolute -top-6 -right-6 w-12 h-12 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-lg"
                >
                  <Star className="w-6 h-6 text-white" />
                </motion.div>

                <motion.div
                  animate={{
                    y: [-5, 5, -5],
                    rotate: [0, -360],
                  }}
                  transition={{
                    y: {
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: 1
                    },
                    rotate: {
                      duration: 2.5,
                      repeat: Infinity,
                      ease: 'linear',
                      delay: 1
                    }
                  }}
                  className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg"
                >
                  <Zap className="w-6 h-6 text-white" />
                </motion.div>

                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                >
                  <div className="text-6xl mb-4">🎉</div>

                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-4xl font-bold bg-gradient-to-r from-yellow-600 to-yellow-700 bg-clip-text text-transparent mb-2"
                  >
                    ¡EMPATE!
                  </motion.h1>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="text-xl text-gray-700 dark:text-gray-300 mb-4"
                  >
                    ¡Felicidades! 🏆
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8, type: 'spring' }}
                    className="space-y-2 text-sm text-gray-600 dark:text-gray-400"
                  >
                    <p>✨ Ganancias calculadas automáticamente</p>
                    <p>🔄 Ciclo reiniciado al Paso 1</p>
                    <p>💰 Saldos actualizados</p>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {Array.from({ length: 20 }).map((_, i) => (
            <motion.div
              key={`sparkle-${i}`}
              initial={{
                x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
                y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000),
                scale: 0,
                opacity: 0
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
                rotate: [0, 180, 360]
              }}
              transition={{
                duration: 2,
                delay: Math.random() * 2,
                repeat: Infinity,
                repeatDelay: Math.random() * 3
              }}
              className="absolute w-2 h-2 bg-yellow-400 rounded-full"
              style={{
                boxShadow: '0 0 6px currentColor'
              }}
            />
          ))}

          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={`firework-${i}`}
              initial={{
                x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
                y: typeof window !== 'undefined' ? window.innerHeight : 1000,
                scale: 0
              }}
              animate={{
                y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight * 0.5 : 500),
                scale: [0, 1, 0]
              }}
              transition={{
                duration: 1.5,
                delay: i * 0.3,
                ease: 'easeOut'
              }}
              className="absolute"
            >
              {Array.from({ length: 12 }).map((_, j) => (
                <motion.div
                  key={j}
                  initial={{ x: 0, y: 0, opacity: 1 }}
                  animate={{
                    x: Math.cos((j * 30) * Math.PI / 180) * 50,
                    y: Math.sin((j * 30) * Math.PI / 180) * 50,
                    opacity: 0
                  }}
                  transition={{
                    duration: 1,
                    delay: i * 0.3 + 0.5,
                    ease: 'easeOut'
                  }}
                  className="absolute w-1 h-1 bg-white rounded-full"
                  style={{
                    boxShadow: `0 0 4px ${['#22c55e', '#3b82f6', '#f59e0b', '#ef4444'][j % 4]}`
                  }}
                />
              ))}
            </motion.div>
          ))}
        </div>
      )}
    </AnimatePresence>
  );
};

export default CelebrationAnimation;